import {naturalSortByKey} from '@benchling/js-utils';
import React from 'react';

import isArchived from 'archive/isArchived';
import useAllRequestTaskSchemasQuery from 'requests/graphql/useAllRequestTaskSchemasQuery';
import {
  RequestSchema,
  SCHEMA_ID_REQUEST_SCHEMA_ALL,
  SCHEMA_OPTION_REQUEST_SCHEMA_ALL,
} from 'requests/RequestConstants';
import {SCHEMA_ID_REQUEST_TASK_SCHEMA_ALL} from 'requests/tasks/RequestTaskConstants';
import {SingleValueFilter} from 'search/SearchConstants';
import pickDefined from 'util/pickDefined';

import {RequestTaskSchemaGraphQL} from '../../../graphql-fragments/requests/schemas/__generated/RequestTaskSchemaGraphQL';
import DropdownFilterForm, {prefixedToggleText} from './base/DropdownFilterForm';
import {BASE_FILTER_FORM_PROP_NAMES, BaseFilterFormProps, FilterStyle} from './base/FilterFormConstants';
import useFilterArchivedDropdownOptions, {
  ArchivableOptionData,
} from './base/useFilterArchivedDropdownOptions';

export function getRequestTaskSchemasForRequestSchema(
  allTaskSchemas: Array<RequestTaskSchemaGraphQL>,
  selectedAssaySchema: RequestSchema | typeof SCHEMA_OPTION_REQUEST_SCHEMA_ALL | null
): Array<RequestTaskSchemaGraphQL> {
  if (!selectedAssaySchema || selectedAssaySchema.id === SCHEMA_ID_REQUEST_SCHEMA_ALL) {
    return allTaskSchemas;
  }
  return allTaskSchemas.filter((taskSchema) =>
    // TODO(TypeScript): RequestSchema type should match any object where id != SCHEMA_ID_REQUEST_SCHEMA_ALL.
    (selectedAssaySchema as RequestSchema).requestTaskSchemaIds.includes(taskSchema.id)
  );
}

interface QueryingRequestTaskSchemaFilterFormProps extends BaseFilterFormProps {
  filter: SingleValueFilter;
  filterStyle: FilterStyle;
  onChangeFilter: (filter: SingleValueFilter) => void;
  onRemove?: () => void;
}

interface RequestTaskSchemaFilterFormProps extends QueryingRequestTaskSchemaFilterFormProps {
  allTaskSchemas: Array<RequestTaskSchemaGraphQL>;
}

export interface RequestTaskSchemaFilterFormExtraProps {
  selectedAssaySchema: RequestSchema | typeof SCHEMA_OPTION_REQUEST_SCHEMA_ALL | null;
}

function _buildOptionDatas(
  allTaskSchemas: Array<RequestTaskSchemaGraphQL>,
  validTaskSchemas: Array<RequestTaskSchemaGraphQL>
): Array<ArchivableOptionData> {
  return [
    ...[{name: 'Any', value: SCHEMA_ID_REQUEST_TASK_SCHEMA_ALL, isArchived: false}],
    ...naturalSortByKey(
      allTaskSchemas.map((requestTaskSchema) => ({
        name: requestTaskSchema.name,
        value: requestTaskSchema.id,
        hidden: !validTaskSchemas.includes(requestTaskSchema),
        isArchived: isArchived(requestTaskSchema),
      })),
      'name'
    ),
  ];
}

/** @package */
export function RequestTaskSchemaFilterForm(
  props: RequestTaskSchemaFilterFormProps & RequestTaskSchemaFilterFormExtraProps
): JSX.Element {
  const {allTaskSchemas, selectedAssaySchema, filter, filterStyle, onChangeFilter, onRemove} = props;
  const optionDatas = _buildOptionDatas(
    props.allTaskSchemas,
    getRequestTaskSchemasForRequestSchema(allTaskSchemas, selectedAssaySchema)
  );
  const onChangeValueBase = (value: string): void => {
    onChangeFilter({...filter, value});
  };
  const filterArchivedDropdownOptionProps = useFilterArchivedDropdownOptions(
    optionDatas,
    'Show archived tasks',
    onChangeValueBase
  );

  const skeletonProps = pickDefined(props, ...BASE_FILTER_FORM_PROP_NAMES);
  return (
    <DropdownFilterForm
      {...skeletonProps}
      {...filterArchivedDropdownOptionProps}
      dropdownToggleText={prefixedToggleText('Task', optionDatas, filter)}
      filterStyle={filterStyle}
      isMulti={false}
      isSearchable={true}
      onRemove={onRemove}
      value={filter.value}
    />
  );
}

export default function QueryingRequestTaskSchemaFilterForm(
  props: QueryingRequestTaskSchemaFilterFormProps & RequestTaskSchemaFilterFormExtraProps
): JSX.Element {
  const taskSchemas = useAllRequestTaskSchemasQuery();
  return (
    <RequestTaskSchemaFilterForm
      {...props}
      allTaskSchemas={taskSchemas ?? []}
      isLoading={taskSchemas == null}
      isDisabled={taskSchemas == null}
    />
  );
}
